import React from 'react';
import ReactDOM from 'react-dom/client';
import 'leaflet/dist/leaflet.css';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

const RESIZE_OBSERVER_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
];

const isIgnoredResizeObserverError = (message: string) => {
  return RESIZE_OBSERVER_MESSAGES.some((knownMessage) => message.includes(knownMessage));
};

const patchResizeObserver = () => {
  const NativeResizeObserver = window.ResizeObserver;
  if (!NativeResizeObserver) {
    return;
  }

  class SafeResizeObserver {
    private observer: ResizeObserver;

    constructor(callback: ResizeObserverCallback) {
      this.observer = new NativeResizeObserver((entries, observer) => {
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }

    observe(target: Element, options?: ResizeObserverOptions) {
      this.observer.observe(target, options);
    }

    unobserve(target: Element) {
      this.observer.unobserve(target);
    }

    disconnect() {
      this.observer.disconnect();
    }
  }

  (window as typeof window & { ResizeObserver: typeof ResizeObserver }).ResizeObserver =
    SafeResizeObserver as unknown as typeof ResizeObserver;
};

patchResizeObserver();

const suppressResizeObserverError = (event: ErrorEvent) => {
  const directMessage = event.message || '';
  const nestedMessage = (event.error as Error | undefined)?.message || '';
  const message = directMessage || nestedMessage;

  if (isIgnoredResizeObserverError(message)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
};

window.addEventListener('error', suppressResizeObserverError);
window.addEventListener('error', suppressResizeObserverError, true);

const originalConsoleError = console.error.bind(console);
console.error = (...args: unknown[]) => {
  const message = args
    .map((value) => (typeof value === 'string' ? value : (value as { message?: string } | undefined)?.message || ''))
    .join(' ');

  if (isIgnoredResizeObserverError(message)) {
    return;
  }

  originalConsoleError(...args);
};

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message =
    typeof reason === 'string'
      ? reason
      : (reason as { message?: string } | undefined)?.message || '';

  if (isIgnoredResizeObserverError(message)) {
    event.preventDefault();
  }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <App />
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
